import { Document, Image } from '@ixigo/icons';
import Button from '@ixigo/iui/Button';
import Chip from '@ixigo/iui/Chip';
import Typography from '@ixigo/iui/Typography';
import { InputType } from '../../enums/conversation';
import Dialog from '../Dialog';
import SpinnerLoader from '../Loader/Spinner';
import { ListItem } from '../RadioList/index.types';
import { FormReviewProps } from './index.types';

const getFileType = (type: string) => (type.includes('image') ? 'image' : 'document');

const FormReview = ({
    elements,
    open,
    value,
    onClose,
    onSubmit,
    loading,
    reviewDialogDescription,
    reviewDialogTitle,
    submitBtnLabel,
}: FormReviewProps) => (
    <Dialog open={open} classes={{ container: '!min-w-full' }}>
        <div className='flex flex-col gap-15 grow p-20'>
            <div className='flex flex-col gap-5'>
                <Typography variant='h4' className='font-medium'>
                    {reviewDialogTitle || 'Review'}
                </Typography>
                <Typography className='text-neutral-600'>{reviewDialogDescription}</Typography>
            </div>
            <div className='flex flex-col gap-15'>
                {elements.map((element, index) => {
                    const val = value[index];
                    if (
                        val === undefined ||
                        val === null ||
                        (typeof val === 'string' && val.trim() === '') ||
                        (Array.isArray(val) && val.length === 0)
                    ) {
                        return null;
                    }

                    let displayValue = '';
                    let fileDisplay: JSX.Element | null = null;

                    if (element.type === InputType.FILE) {
                        if (val instanceof File) {
                            fileDisplay = (
                                <Chip
                                    className='w-fit !px-10 !py-5'
                                    iconLeft={getFileType(val.type) === 'image' ? <Image /> : <Document />}
                                    variant='soft'
                                    color='neutral'
                                >
                                    {val.name}
                                </Chip>
                            );
                        } else if (Array.isArray(val) && val.every((item): item is File => item instanceof File)) {
                            fileDisplay = (
                                <div className='flex flex-wrap gap-5'>
                                    {val.map((file) => (
                                        <Chip
                                            key={`${file.name}-${file.size}-${file.lastModified}`}
                                            className='w-fit !px-10 !py-5'
                                            iconLeft={getFileType(file.type) === 'image' ? <Image /> : <Document />}
                                            variant='soft'
                                            color='neutral'
                                        >
                                            {file.name}
                                        </Chip>
                                    ))}
                                </div>
                            );
                        }
                    } else if (
                        Array.isArray(val) &&
                        val.every((item): item is ListItem => 'id' in item && 'label' in item && 'value' in item)
                    ) {
                        displayValue = val.map((item) => item.label).join(', ');
                    } else if (typeof val === 'string') {
                        displayValue = val;
                    } else if (
                        typeof val === 'object' &&
                        val !== null &&
                        'id' in val &&
                        'label' in val &&
                        'value' in val
                    ) {
                        displayValue = (val as ListItem).label;
                    }

                    return (
                        <div key={element.id} className='p-10 bg-neutral-40 rounded-10'>
                            <div className='flex flex-col gap-5'>
                                <Typography className='font-medium'>{element.label}</Typography>
                                {fileDisplay || <Typography className='text-neutral-600'>{displayValue}</Typography>}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
        <div className='flex gap-20 border-t px-20 py-10'>
            <Button variant='soft' size='lg' className='w-full' onClick={onClose} disabled={loading}>
                Edit
            </Button>
            <Button size='lg' className='w-full' onClick={onSubmit} disabled={loading}>
                {loading ? <SpinnerLoader className='px-20' size='sm' /> : submitBtnLabel || 'Submit'}
            </Button>
        </div>
    </Dialog>
);

export default FormReview;
